import { useEffect, useState } from "react";

// Animated top-down diagram of a French péage plaza for the toll-roads
// guide. A motorbike approaches, merges into the lane that matches the
// current payment scenario, waits for the barrier, and rolls through.
// Scenarios auto-cycle; lane colours and markers match the LANE_CARDS
// below the diagram. With prefers-reduced-motion the diagram renders
// static with the bike at the green (safe-default) lane.

const LANES = [
    { marker: "t", bg: "#e07a2e", fg: "#fff", x: 135 },
    { marker: "CB", bg: "#1d63b8", fg: "#fff", x: 285 },
    { marker: "€", bg: "#c9a227", fg: "#3a2a0a", x: 435 },
    { marker: "↓", bg: "#2f8f4e", fg: "#fff", x: 585 },
];

const SCENARIOS = [
    {
        lane: 0,
        label: "Got a Liber-t tag?",
        sub: "Orange 't' lane — walking pace, the gantry beeps, barrier lifts.",
    },
    {
        lane: 1,
        label: "Paying by card?",
        sub: "Blue CB lane — tap or insert. Contactless means the glove stays on.",
    },
    {
        lane: 2,
        label: "Paying cash, or need a human?",
        sub: "Coins-symbol lane — change machine or staffed booth.",
    },
    {
        lane: 3,
        label: "Not sure?",
        sub: "Green arrow — takes every payment method. The safe default.",
    },
];

const CYCLE_MS = 5000;

function Bike() {
    return (
        <g className="gt-tla__bike-shape">
            <ellipse cx="0" cy="15" rx="4.5" ry="8.5" fill="#1c2433" />
            <ellipse cx="0" cy="-15" rx="4" ry="7.5" fill="#1c2433" />
            <rect x="-5.5" y="-11" width="11" height="23" rx="5" fill="var(--gt-primary)" />
            <line x1="-9" y1="-9" x2="9" y2="-9" stroke="#1c2433" strokeWidth="2.5" strokeLinecap="round" />
            <circle cx="0" cy="1" r="5" fill="var(--gt-accent)" />
        </g>
    );
}

export default function TollLaneAnimation() {
    const [scenario, setScenario] = useState(0);
    const [reduced, setReduced] = useState(false);

    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            setReduced(true);
            setScenario(3); // static fallback shows the green-arrow safe default
            return;
        }
        const id = window.setInterval(
            () => setScenario((s) => (s + 1) % SCENARIOS.length),
            CYCLE_MS,
        );
        return () => window.clearInterval(id);
    }, []);

    const active = SCENARIOS[scenario];
    const lane = LANES[active.lane];

    return (
        <figure className="gt-tla" aria-hidden="true">
            <div className="gt-tla__caption" key={`cap-${scenario}`}>
                <span
                    className="gt-tla__caption-marker"
                    style={{ background: lane.bg, color: lane.fg }}
                >
                    {lane.marker}
                </span>
                <span>
                    <strong>{active.label}</strong> {active.sub}
                </span>
            </div>
            <svg
                viewBox="0 0 720 400"
                width="100%"
                className={reduced ? "gt-tla__svg gt-tla__svg--static" : "gt-tla__svg"}
            >
                {/* Road: approach fanning out into four gate lanes */}
                <path
                    d="M280 400 L60 240 L60 60 L660 60 L660 240 L440 400 Z"
                    fill="#b8b2a4"
                />
                {/* Lane divider dashes inside the plaza */}
                {[210, 360, 510].map((x) => (
                    <line
                        key={x}
                        x1={x}
                        y1={230}
                        x2={x}
                        y2={110}
                        stroke="#f7f4ee"
                        strokeWidth="3"
                        strokeDasharray="10 12"
                    />
                ))}
                {/* Booth islands between lanes */}
                {[210, 360, 510].map((x) => (
                    <rect
                        key={x}
                        x={x - 11}
                        y={92}
                        width={22}
                        height={116}
                        rx={11}
                        fill="#efece4"
                        stroke="#d8d2c4"
                        strokeWidth="1.5"
                    />
                ))}
                {/* Barriers: one across each lane, hinged at its island */}
                {LANES.map((l, i) => (
                    <rect
                        key={l.marker}
                        className={
                            !reduced && i === active.lane
                                ? "gt-tla__barrier gt-tla__barrier--opening"
                                : "gt-tla__barrier"
                        }
                        x={l.x - 58}
                        y={186}
                        width={116}
                        height={6}
                        rx={3}
                        fill="#c1463b"
                        style={{ transformOrigin: `${l.x - 58}px 189px` }}
                    />
                ))}
                {/* Gantry with per-lane overhead signs */}
                <rect x={40} y={28} width={640} height={52} rx={12} fill="var(--gt-primary)" />
                <text
                    x={360}
                    y={22}
                    textAnchor="middle"
                    fontFamily="var(--gt-font-display), Georgia, serif"
                    fontSize="16"
                    fill="var(--gt-text-muted)"
                >
                    Péage
                </text>
                {LANES.map((l) => (
                    <g key={l.marker}>
                        <circle cx={l.x} cy={54} r={17} fill={l.bg} />
                        <text
                            x={l.x}
                            y={54}
                            textAnchor="middle"
                            dominantBaseline="central"
                            fontSize={l.marker.length > 1 ? 13 : 17}
                            fontWeight="700"
                            fill={l.fg}
                            fontFamily="var(--gt-font-body), sans-serif"
                        >
                            {l.marker}
                        </text>
                    </g>
                ))}
                {/* The bike. Keyed by scenario so the ride restarts each cycle. */}
                <g
                    key={`bike-${scenario}`}
                    className={reduced ? "gt-tla__bike gt-tla__bike--static" : "gt-tla__bike"}
                    style={{ "--tla-x": `${lane.x}px` } as React.CSSProperties}
                >
                    <Bike />
                </g>
            </svg>
        </figure>
    );
}
